// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import React, { FormEvent, RefObject, SyntheticEvent } from "react";
import {
    getDarkTheme,
    getPrimaryBlueTheme,
    getPrimaryRedTheme,
} from "../../../../common/themes";
import {
    Checkbox,
    Customizer,
    Dialog,
    DialogContent,
    DialogFooter,
    DialogType,
    ICustomizations,
    ITheme,
    PrimaryButton,
    Spinner,
    SpinnerSize,
} from "@fluentui/react";
import axios from "axios";
import { IAdHocProject, ILabel, IProject } from "../../../../models/applicationState";
import { parseEndpointByType } from "../jsonSchemaFormHelper";
//import Canvas from "../../pages/editorPage/canvas";
import Canvas from "../../pages/adhococrv2/canvas";
import { ModalHeader } from "reactstrap";
import "./searchDialog.scss";
/**
 * Properties for Alert Component
 * @member closeButtonText - Text displayed on 'Close' button. Default 'OK'
 * @member onClose - Function to execute on alert close
 * @member confirmButtonTheme - Theme of 'Confirm' button
 */
export interface ISearchDialog {
    closeButtonText?: string;
    confirmButtonTheme?: ITheme;
    show?: boolean;
    fileName?: string;
    container?: IAdHocProject;
    onClose?: () => void;
    onSearchClick?: (sender: SyntheticEvent, value: ILabel) => void;
    clearSearch?: () => void;
    currentPage: number;
    canvas: RefObject<Canvas>;
}

/**
 * State for Alert Component
 * @member params - Arguments passed in the open command
 */
export interface ISearchDialogState {
    searching?: boolean;
    searchResult: ILabel[];
    searchAll: boolean;
    keyword: string;
    isOCRRunning: boolean;
}

/**
 * @name - Alert
 * @description - Generic Alert dialog
 */
export default class SearchDialog extends React.Component<
    ISearchDialog,
    ISearchDialogState
> {
    // hachet
    constructor(props, context) {
        super(props, context);

        this.state = {
            searching: false,
            searchResult: [],
            searchAll: false,
            keyword: "",
            isOCRRunning: false,
        };

        this.onCloseClick = this.onCloseClick.bind(this);
        this.onSearchClick = this.onSearchClick.bind(this);
    }

    private inputRef = React.createRef<HTMLInputElement>();

    public render() {
        const dark: ICustomizations = {
            settings: {
                theme: getDarkTheme(),
            },
            scopedSettings: {},
        };

        return (
            //<Customizer {...dark}>
                <Dialog
                    onDismiss={this.onCloseClick}
                    hidden={!this.props.show}
                    dialogContentProps={{
                        type: DialogType.normal,
                        title: "Search the document",
                        isMultiline: true,
                    }}
                    minWidth="400px"
                    modalProps={{
                        isBlocking: false,
                        styles: {
                            main: {
                                backgroundColor: "#f9f9f9", // Off-white background
                            },
                        },
                    }}
                >
                    <DialogContent className="searchModal">
                        <input
                            className="tag-input-box"
                            style={{ marginRight: "10px", width: "50%" }}
                            type="text"
                            placeholder="Type a word to search"
                            autoFocus={true}
                            value={this.state.keyword}
                            disabled={
                                this.state.searching || this.state.isOCRRunning
                            }
                            onChange={this.inputChanged}
                            ref={this.inputRef}
                        />
                        <PrimaryButton
                            theme={getPrimaryBlueTheme()}
                            onClick={this.onSearchClick}
                            disabled={this.state.keyword === ""}
                            height="30px"
                        >
                            {this.state.searching ? (
                                <Spinner
                                    size={SpinnerSize.small}
                                    label="Searching..."
                                    ariaLive="off"
                                    labelPosition="right"
                                />
                            ) : this.state.isOCRRunning ? (
                                ""
                            ) : (
                                "Search"
                            )}
                            {this.state.isOCRRunning ? (
                                <Spinner
                                    size={SpinnerSize.small}
                                    label="Running Layout..."
                                    ariaLive="off"
                                    labelPosition="right"
                                />
                            ) : (
                                ""
                            )}
                        </PrimaryButton>

                        {this.state.searchResult.length > 0 && (
                            <table
                                className="accuracy-table table-md"
                                style={{ marginTop: "10px" }}
                            >
                                <tbody>
                                    <tr>
                                        <th className="tag-name">Keywords</th>
                                        <th
                                            className="text-right"
                                            style={{
                                                paddingLeft: "10px",
                                                paddingRight: "10px",
                                            }}
                                        >
                                            Page
                                        </th>
                                        <th className="text-right"></th>
                                    </tr>
                                    {this.state.searchResult.map((entry) => (
                                        <tr key={entry.id}>
                                            <td className="tag-name">
                                                {entry.label}
                                            </td>
                                            <td
                                                className="text-center"
                                                style={{
                                                    paddingLeft: "10px",
                                                    paddingRight: "10px",
                                                }}
                                            >
                                                {entry.value[0].page}
                                            </td>
                                            <td
                                                className="text-right"
                                                style={{
                                                    paddingLeft: "10px",
                                                    paddingRight: "10px",
                                                }}
                                            >
                                                <PrimaryButton
                                                    theme={getPrimaryBlueTheme()}
                                                    onClick={() =>
                                                        this.focusPage(
                                                            entry as ILabel
                                                        )
                                                    }
                                                >
                                                    View
                                                </PrimaryButton>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </DialogContent>
                    <DialogFooter>
                        <PrimaryButton
                            style={{ marginRight: "10px" }}
                            theme={getPrimaryRedTheme()}
                            onClick={this.clearSearchClick}
                            text="Clear"
                            styles={{
                                root: {
                                    border: "1px solid #808080", // Red border for the "Clear" button
                                    borderRadius: "4px", // Optional: Add rounded corners
                                },
                            }}
                        />
                        <PrimaryButton
                            theme={getDarkTheme()}
                            onClick={this.onCloseClick}
                            text="Close"
                            styles={{
                                root: {
                                    border: "1px solid #808080", // Red border for the "Clear" button
                                    borderRadius: "4px", // Optional: Add rounded corners
                                },
                            }}
                        />
                    </DialogFooter>
                </Dialog>
            //</Customizer>
        );
    }

    private inputChanged = (ev?: FormEvent<HTMLElement | HTMLInputElement>) => {
        this.setState({
            keyword: this.inputRef.current.value,
        });
    };

    private onSearchAllCheckChanged = (
        ev?: FormEvent<HTMLElement | HTMLInputElement>,
        checked?: boolean
    ) => {
        this.setState({
            searchAll: checked,
        });
    };

    private clearSearchClick = () => {
        this.clearState();
        this.props.clearSearch();
    };

    public clearState() {
        this.setState({
            searching: false,
            searchResult: [],
            keyword: "",
            isOCRRunning: false,
            searchAll: false,
        });
    }

    private onSearchClick() {
        this.props.clearSearch();
        this.search();
    }

    private focusPage(label: ILabel) {
        this.props.onSearchClick(null, label);
    }

    private async search() {
        // if (this.state.searchAll) {
        //     if (!this.props.canvas.current.areAllPagesOCR()) {
        //         this.setState({
        //             isOCRRunning: true,
        //             searchResult: [],
        //         });
        //         await this.props.canvas.current.loadOcr(true);
        //     }
        // }

        this.setState({
            searching: true,
            searchResult: [],
            isOCRRunning: false,
        });

        //const sas = this.props.container.sourceConnection.providerOptions["sas"];

        try {
            const ocrStr = this.props.container.ocrs[0].ocr;
            const ocr = JSON.parse(ocrStr);

            let textFeatures = [];
            const imageExtent = this.props.canvas.current.getImageExtent();
            const keyword = this.state.keyword.toLowerCase();
            for(let i = 0; i < ocr.analyzeResult.pages.length; i++) {
                var page = ocr.analyzeResult.pages[i];
                for(let j = 0; j < page.words.length; j ++) {
                    var word = page.words[j];
                    if (word.content.toLowerCase().includes(keyword)) {
                        const ocrExtent = [
                            0,
                            0,
                            page.width,
                            page.height,
                        ];
                        textFeatures.push(
                            this.props.canvas.current.createBoundingBoxVectorFeature(
                                word.content,
                                word.polygon,
                                imageExtent,
                                ocrExtent,
                                page.pageNumber
                            )
                        )
                    }
                }
            } 

            // const allLabels = this.props.canvas.current.getAllFeatures();
            // const localSearchResult = allLabels.filter(r => r.values_.text.toLowerCase().includes(this.state.keyword.toLowerCase()));
            const searchResult = textFeatures.map(r => { 
                const page = r.id_.split(':')[1];
                return {
                    label: r.values_.text,
                    value: [
                        {
                            page: parseInt(page),
                            text: r.values_.text,
                            boundingBoxes: [
                                r.values_.boundingbox
                            ]
                        }
                    ]
                }
            });
            this.setState({
                searchResult: searchResult as ILabel[], 
                searching: false
            });            
        } catch (error) {
            console.log(error);
        }
    }

    private onCloseClick() {
        if (this.props.onClose) {
            this.props.onClose.apply(null, null);
        }
    }
}
